import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Checkbox, Field, Input, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { useQuery } from '@apollo/client';
import { OTHER } from '../../../../app/common/constants/SiteConstants';
import IAuditStatusFormProps from './IAuditStatusFormProps';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import IFieldsetProps from '../../../../utils/types/IFieldsetProps';
import GET_AUDIT_STATUSES_QUERY from '../../../../utils/statApi/AuditStatusApi';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';
import AuditingFirmsDropdown from './AuditingFirmsDropdown';

const AuditStatusDropdown = (props: IFieldsetProps): JSX.Element => {
  const { value, label, errors, handleChange, valueKey, outputKey } = props;
  const { loading, data } = useQuery(GET_AUDIT_STATUSES_QUERY);
  const { auditStatuses } = !loading && data ? data : [];

  return (
    <>
      <QueryBasedDropdown
        value={value as IDropdownReference}
        label={label}
        outputKey={outputKey}
        dropdownOptionsData={auditStatuses}
        errors={errors}
        handleChange={handleChange}
        valueKey={valueKey}
      />
    </>
  );
};

const AuditStatusForm = ({
  auditStatus,
  errors,
  handleClose,
  handleChange,
}: IAuditStatusFormProps): JSX.Element => {
  const {
    auditStatus: auditStatusOption,
    statusDate,
    auditingFirm,
    otherAuditingFirm,
    auditLegallyRequired,
  } = auditStatus;
  const [auditFirmState, setAuditFirmState] = useState(auditingFirm?.fieldName);
  const [otherAuditFirmState, setOtherAuditFirmState] = useState(otherAuditingFirm);
  const [selectedDate, setSelectedDate] = useState(statusDate?.toString());

  const handleSelectDate = (dateString: string): void => {
    handleChange(dateString, 'statusDate');
    setSelectedDate(dateString);
  };
  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <AuditStatusDropdown
          label="Audit Status"
          valueKey="auditStatus"
          errors={errors}
          handleChange={handleChange}
          value={auditStatusOption}
        />
        <UtcDatePicker
          label="Status Date"
          isRequired
          allowTextInput
          textField={{
            name: 'statusDate',
            errorMessage: errors?.statusDate?.message,
          }}
          value={selectedDate}
          onSelectDate={handleSelectDate}
        />
        <AuditingFirmsDropdown
          label="Auditing Firm"
          valueKey="auditingFirm"
          errors={errors}
          value={auditingFirm}
          handleChange={(newValue): void => {
            setAuditFirmState(newValue.fieldName);
            if (newValue.fieldName !== OTHER) {
              setOtherAuditFirmState(null);
              handleChange(null, 'otherAuditingFirm');
            }
            handleChange(newValue, 'auditingFirm');
          }}
        />
        <Field label="Other Auditing Firm">
          <Input
            aria-label="Other Auditing Firm"
            disabled={auditFirmState !== OTHER}
            value={otherAuditFirmState || ''}
            onChange={(e, data): void => {
              setOtherAuditFirmState(data.value);
              handleChange(data.value, 'otherAuditingFirm');
            }}
          />
        </Field>
        <Checkbox
          aria-label="Audit Legally Required"
          label="Audit Legally Required"
          defaultChecked={auditLegallyRequired}
          onChange={(e, data): void => {
            handleChange(data.checked, 'auditLegallyRequired');
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default AuditStatusForm;
